import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import { Button, Card, Checkbox, Switch, Box } from "@mui/material";
import axios from "../../api";
import SoftBox from "components/SoftBox";
import AddAssetType from "./AddAssetType";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import SlideOverPanel from "components/SlideOverPanel";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftButton from "components/SoftButton";
import BulkUploadAssetType from "./BulkUploadAssetType";
import { CloudUpload } from "@mui/icons-material";
import { AxiosError } from "axios";
import { useSearchParams } from "react-router-dom";

// Types
interface AssetTypeData {
  asset_type_id: number;
  asset_type_name: string;
  client_id: number;
  created_at: string;
  is_active: number;
  updated_at: string;
}

interface SelectedRow {
  dataIndex: number;
}

interface TableMeta {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: AssetTypeData[];
}

interface ColumnMeta {
  label?: string;
  name: string;
  index: number;
}

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Extracted Component Definitions (OUTSIDE the main component)
interface HeaderCheckboxProps {
  filteredAssets: any[];
  selectedRows: { dataIndex: number }[];
  handleCheckAllboxes: (checked: boolean) => void;
}

const HeaderCheckbox: React.FC<HeaderCheckboxProps> = ({
  filteredAssets,
  selectedRows,
  handleCheckAllboxes,
}) => (
  <th
    style={{
      padding: "16px",
      textAlign: "left",
      backgroundColor: "#f8f9fa",
      borderBottom: "1px solid #e0e0e0",
    }}
  >
    <Checkbox
      checked={
        !!filteredAssets &&
        filteredAssets.length > 0 &&
        selectedRows.length === filteredAssets.length
      }
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        handleCheckAllboxes(event.target.checked)
      }
    />
  </th>
);

interface BodyCheckboxProps {
  rowIndex: number;
  selectedRows: { dataIndex: number }[];
  handleCheckboxChange: (rowIndex: number) => void;
}

const BodyCheckbox: React.FC<BodyCheckboxProps> = ({
  rowIndex,
  selectedRows,
  handleCheckboxChange,
}) => (
  <Checkbox
    checked={selectedRows.some((row) => row.dataIndex === rowIndex)}
    onChange={() => handleCheckboxChange(rowIndex)}
  />
);

interface StatusSwitchCellProps {
  value: number;
  data: any;
  handleStatus: (data: any, checked: boolean) => void;
}

const StatusSwitchCell: React.FC<StatusSwitchCellProps> = ({
  value,
  data,
  handleStatus,
}) => (
  <Switch
    checked={!!value}
    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
      handleStatus(data, event.target.checked)
    }
    color="primary"
  />
);

// Utility functions
const formatDate = (dateString: string | number | Date) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

// Renderer functions
const renderDateCell = (value: string) => formatDate(value);

const renderHeaderCheckbox = (
  filteredAssets: any[],
  selectedRows: SelectedRow[],
  handleCheckAllboxes: (checked: boolean) => void
) => (
  <HeaderCheckbox
    filteredAssets={filteredAssets}
    selectedRows={selectedRows}
    handleCheckAllboxes={handleCheckAllboxes}
  />
);

const renderBodyCheckbox = (
  rowIndex: number,
  selectedRows: SelectedRow[],
  handleCheckboxChange: (rowIndex: number) => void
) => (
  <BodyCheckbox
    rowIndex={rowIndex}
    selectedRows={selectedRows}
    handleCheckboxChange={handleCheckboxChange}
  />
);

const renderStatusSwitch = (
  value: number,
  tableMeta: TableMeta,
  handleStatus: (data: any, checked: boolean) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];

  return (
    <StatusSwitchCell value={value} data={data} handleStatus={handleStatus} />
  );
};

const AssetTypeList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const [assetData, setAssetData] = React.useState<AssetTypeData[] | null>(
    null
  );
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [selectedRows, setSelectedRows] = React.useState<SelectedRow[]>([]);
  const { fetchError, fetchSuccess } = useSnackbar();
  const [isBulkUploadOpen, setBulkUploadOpen] = React.useState(false);

  const filteredAssets = React.useMemo(() => {
    if (!assetData) return [];

    if (status === "active") {
      return assetData.filter((c) => Number(c.is_active) === 1);
    }

    if (status === "inactive") {
      return assetData.filter((c) => Number(c.is_active) === 0);
    }

    return assetData;
  }, [assetData, status]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleStatus = async (data: AssetTypeData, isActive: boolean) => {
    try {
      const response = await axios.put(`/asset-types/${data?.asset_type_id}`, {
        is_active: isActive,
      });
      fetchData();
      fetchSuccess(response.data.message);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/asset-types");
      setAssetData(response?.data?.list);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const handleBulkStatusUpdate = async (isActive: boolean): Promise<void> => {
    if (!filteredAssets) return;

    try {
      const promises = selectedRows.map(async (row) => {
        const asset = filteredAssets[row.dataIndex];
        return axios.put(`/asset-types/${asset?.asset_type_id}`, {
          is_active: isActive,
        });
      });
      await Promise.all(promises);
      setSelectedRows([]);
      fetchData();
      fetchSuccess(isActive ? "Asset activated" : "Asset deactivated");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleCheckAllboxes = (checked: boolean): void => {
    if (!filteredAssets) return;

    setSelectedRows(
      checked ? filteredAssets.map((_, index) => ({ dataIndex: index })) : []
    );
  };

  const handleCheckboxChange = (rowIndex: number): void => {
    setSelectedRows((prevSelectedRows) => {
      const isSelected = prevSelectedRows.some(
        (row) => row.dataIndex === rowIndex
      );
      return isSelected
        ? prevSelectedRows.filter((row) => row.dataIndex !== rowIndex)
        : [...prevSelectedRows, { dataIndex: rowIndex }];
    });
  };

  const handleBulkUploadOpen = () => {
    setBulkUploadOpen(true);
  };

  const handleBulkUploadClose = () => {
    setBulkUploadOpen(false);
  };

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      {
        name: "checkbox",
        label: "",
        options: {
          filter: false,
          sort: false,
          customHeadRender: () =>
            renderHeaderCheckbox(
              filteredAssets,
              selectedRows,
              handleCheckAllboxes
            ),
          customBodyRender: (value: unknown, tableMeta: TableMeta) =>
            renderBodyCheckbox(
              tableMeta.rowIndex,
              selectedRows,
              handleCheckboxChange
            ),
        },
      },
      { name: "asset_type_name", label: "Asset Type" },
      {
        name: "created_at",
        label: "Created At",
        options: {
          filter: false,
          customBodyRender: renderDateCell,
        },
      },
      {
        name: "is_active",
        label: "Status",
        options: {
          filter: false,
          customBodyRender: (value: number, tableMeta: TableMeta) =>
            renderStatusSwitch(value, tableMeta, handleStatus),
        },
      },
    ],
    [filteredAssets, selectedRows]
  );

  return (
    <>
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginRight: "10px",
            gap: "8px",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flext-start", sm: "center" },
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <SoftButton
              variant="gradient"
              color="secondary"
              onClick={handleBulkUploadOpen}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CloudUpload />
                Bulk Upload
              </Box>
            </SoftButton>
            <SoftButton variant="gradient" color="info" onClick={handleOpen}>
              Add Asset Type
            </SoftButton>
          </Box>
          <div
            style={{
              display: "block",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              className="success-btn"
              onClick={() => {
                handleBulkStatusUpdate(true);
              }}
              disabled={selectedRows.length === 0}
              sx={{ marginRight: "10px", marginBottom: "2px" }}
            >
              Activate
            </Button>
            <Button
              variant="contained"
              className="success-btn"
              onClick={() => {
                handleBulkStatusUpdate(false);
              }}
              disabled={selectedRows.length === 0}
            >
              InActivate
            </Button>
          </div>
        </Box>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets m-category"
        >
          {loading ? (
            <PageLoader />
          ) : (
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title={"Manage Asset Types"}
                data={filteredAssets || []}
                columns={columns}
                options={tableOptions}
              />
            </ThemeProvider>
          )}
        </SoftBox>
      </Card>
      <SlideOverPanel
        open={isOpen}
        onClose={handleClose}
        title="Add Asset Type"
        width={1000}
      >
        <AddAssetType
          isOpen={true}
          onClose={handleClose}
          fetchData={() => {
            handleClose();
            fetchData();
          }}
          embedded
        />
      </SlideOverPanel>
      {isBulkUploadOpen && (
        <BulkUploadAssetType
          isOpen={isBulkUploadOpen}
          onClose={handleBulkUploadClose}
          fetchData={fetchData}
        />
      )}
    </>
  );
};

export default AssetTypeList;
